// web/src/tutorial/pages.ts
// 教程页装配：标题区 + 侧边栏（TOC）+ 正文 + 页尾视频 + 上下篇导航。
// Quick Start 与 自定义人物进阶教程 共用本模块。

import { getLang, initI18n } from "../i18n";
import { productMarkBySlug } from "../product-visual";
import { pickText, PRODUCT_META, YOUTUBE_CHANNEL_URL } from "../site-data";
import { buildToc } from "../toc";
import { hasDoc } from "./catalog";
import { bilibiliEmbed, escapeHtml, renderSections, youtubeEmbed } from "./render";
import { mountScreenshotViewer } from "./screenshot-viewer";
import type { Lang } from "../i18n";
import type { TutorialDoc, TutorialKind } from "./types";

/**
 * 注意：本模块**不 import 任何一类的正文注册表**。
 * 四类正文合起来约 2MB，若在这里 import，四个入口 bundle 会各自打进全部正文。
 * 正文由各入口（tutorial-*.ts）按需 import 后传给 initTutorialPage()。
 */

/** 总览类文档的 slug（没有对应产品，不参与上下篇与交叉链接） */
const OVERVIEW_SLUG = "overview";

/**
 * 不是「某个产品」的文档：总览页与 Unreal 的公共模块页。
 * 它们不参与上一篇/下一篇，也不出现在按乐器的入口栅格里。
 */
const NON_PRODUCT_SLUGS = new Set([OVERVIEW_SLUG, "common"]);

const KIND_LABEL: Record<TutorialKind, { zh: string; en: string }> = {
  quickstart: { zh: "快速上手", en: "Quick Start" },
  detail: { zh: "软件界面详细说明", en: "Software UI Reference" },
  advanced: { zh: "自定义人物进阶教程", en: "Custom Performer Tutorial" },
  plugin: { zh: "Blender 插件手册", en: "Blender Plugin Manual" },
  unreal: { zh: "Unreal 插件手册", en: "Unreal Plugin Manual" },
};

const KIND_ROOT: Record<TutorialKind, string> = {
  quickstart: "/quickstart.html",
  detail: "/details.html",
  advanced: "/advanced.html",
  // 两个插件手册各自是一个目录，总览即该目录的 index.html
  plugin: "/manuals/blender/",
  unreal: "/manuals/unreal/",
};

/**
 * 面包屑中间项（"首页 / ??? / 当前页"）。
 * 插件页统一指向平台选择枢纽 /plugin-manuals.html，而不是平台总览自身
 * —— 后者在总览页上就等于「链接到自己」。
 */
const CRUMB_ROOT: Record<TutorialKind, { zh: string; en: string; href: string }> =
  {
    quickstart: { zh: "快速上手", en: "Quick Start", href: "/quickstart.html" },
    detail: {
      zh: "界面详细说明",
      en: "UI Reference",
      href: "/details.html",
    },
    advanced: { zh: "进阶教程", en: "Advanced", href: "/advanced.html" },
    plugin: {
      zh: "插件手册",
      en: "Plugin Manuals",
      href: "/plugin-manuals.html",
    },
    unreal: {
      zh: "插件手册",
      en: "Plugin Manuals",
      href: "/plugin-manuals.html",
    },
  };

const KIND_PATH: Record<TutorialKind, string> = {
  quickstart: "/quickstart/",
  detail: "/details/",
  advanced: "/advanced/",
  plugin: "/manuals/blender/",
  unreal: "/manuals/unreal/",
};

/**
 * 插件页面包屑的第三级（平台手册本身）：
 *   模块页 → 首页 / 插件手册 / Blender 插件手册 / <乐器>
 *   总览页 → 首页 / 插件手册 / MusicDoll Blender 插件（本级即当前页，不再重复）
 * 仅 plugin / unreal 两类需要。
 */
const CRUMB_PLATFORM: Partial<
  Record<TutorialKind, { zh: string; en: string; href: string }>
> = {
  plugin: {
    zh: "Blender 插件手册",
    en: "Blender Plugin Manual",
    href: "/manuals/blender/",
  },
  unreal: {
    zh: "Unreal 插件手册",
    en: "Unreal Plugin Manual",
    href: "/manuals/unreal/",
  },
};

/**
 * 页尾「换平台 / 继续学习」指向的类型：
 * 两个插件手册互为对照（同一件事在另一个平台怎么做）；
 * Quick Start 与「软件界面详细说明」互为补充（先跑通流程，再逐个参数细看）。
 */
const CROSS_KIND: Record<TutorialKind, TutorialKind> = {
  quickstart: "detail",
  detail: "quickstart",
  advanced: "quickstart",
  plugin: "unreal",
  unreal: "plugin",
};

function crossKind(kind: TutorialKind): TutorialKind {
  return CROSS_KIND[kind];
}

/** 跨类入口的按钮文字：直接说明目标页面是干什么的，不用类型名照抄 */
function crossLabel(kind: TutorialKind, lang: Lang): string {
  const isZh = lang === "zh";
  switch (kind) {
    case "detail":
      return isZh ? "逐个界面参数细看" : "Every screen and parameter";
    case "quickstart":
      return isZh ? "最短流程跑通一遍" : "The shortest working flow";
    default:
      return kindLabel(kind, lang);
  }
}

function kindLabel(kind: TutorialKind, lang: Lang): string {
  return KIND_LABEL[kind][lang];
}

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

function renderVideoSection(doc: TutorialDoc, lang: Lang): string {
  if (doc.video.hidden) return "";
  const isZh = lang === "zh";
  const caption = pickText(doc.video.caption, lang);
  // 中文版内嵌 B 站，英文版内嵌 YouTube —— 英文站不出现任何 B 站链接
  const embed = isZh ? bilibiliEmbed(doc.video.url) : youtubeEmbed(doc.video.youtube);
  const watch = isZh ? doc.video.url : doc.video.youtube;
  const player = embed
    ? isZh
      ? `<div class="video-embed"><iframe src="${embed}" scrolling="no" border="0" frameborder="no" framespacing="0" allowfullscreen="true"></iframe></div>`
      : `<div class="video-embed"><iframe src="${embed}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`
    : `<div class="media-placeholder ratio-16-9">
    <span class="tut-ph-badge">${isZh ? "视频待补" : "English video coming soon"}</span>
    <span class="tut-ph-text">${escapeHtml(caption)}</span>
  </div>`;
  // 有播放器时不再给「在 B 站 / YouTube 打开」按钮：播放器自带平台入口，多一个按钮是重复的。
  // 只有本侧没有视频时才给一条兜底入口（中文版保持原来的「视频链接待补」提示）。
  const actions = watch
    ? ""
    : `<div class="feature-actions">${
        isZh
          ? `<span class="muted">视频链接待补</span>`
          : `<a class="btn secondary" target="_blank" rel="noopener noreferrer" href="${YOUTUBE_CHANNEL_URL}">Author's YouTube channel</a>`
      }</div>`;
  const notice = doc.video.placeholder
    ? `<p class="tut-note">${
        isZh
          ? "当前视频为站点占位内容，待替换为该产品的完整操作演示。"
          : "This video is a site-wide placeholder and will be replaced with the product-specific walkthrough."
      }</p>`
    : !watch && !isZh
      ? `<p class="tut-note">The English walkthrough for this page has not been uploaded yet — check the author's YouTube channel for updates.</p>`
      : "";
  // 系列视频：只外链，避免一页内嵌十几个播放器拖慢加载；按语言各取各的链接
  const seriesItems = (doc.video.series || []).filter((s) => (isZh ? s.url : s.youtube));
  const series = seriesItems.length
    ? `<h3 id="video-series">${isZh ? "系列视频" : "Related episodes"}</h3>
<p class="tut-note">${
        isZh
          ? "同一主题的系列教程放在下面，按顺序看更连贯（外链，不在本页播放）。"
          : "Episodes on the same topic, best watched in order (external links, not embedded on this page)."
      }</p>
<ul class="video-list">
${seriesItems
  .map(
    (s) =>
      `<li class="video-link"><a class="video-link-title" href="${escapeHtml(
        (isZh ? s.url : s.youtube) as string,
      )}" target="_blank" rel="noopener noreferrer">${escapeHtml(pickText(s.title, lang))}</a></li>`,
  )
  .join("\n")}
</ul>`
    : "";
  return `<section class="tut-section tut-section-video">
<h2 id="video">${isZh ? "演示视频" : "Demo Video"}</h2>
<p class="tut-p">${escapeHtml(caption)}</p>
${player}
${notice}
${actions}
${series}
</section>`;
}

/**
 * 插件手册总览页的模块入口栅格：每个模块一张卡，指向该平台自己的说明页。
 * 只在 plugin / unreal 总览文档后追加，标题会进入本页大纲。
 * Unreal 平台额外包含附赠模块 Singer（不在产品矩阵里，卡片上会标出来）。
 */
function renderHub(kind: TutorialKind, all: TutorialDoc[], lang: Lang): string {
  const isZh = lang === "zh";
  const isUnreal = kind === "unreal";
  const otherKind = crossKind(kind);
  const docs = all.filter((d) => !NON_PRODUCT_SLUGS.has(d.slug));
  const cards = docs
    .map((d) => {
      const meta = PRODUCT_META.find((p) => p.slug === d.slug);
      const mark = productMarkBySlug(d.slug);
      const note = isUnreal
        ? isZh
          ? "该模块在 Unreal 里的页签、按钮与参数"
          : "This module's tabs, buttons and parameters in Unreal"
        : isZh
          ? "该乐器在 Blender 里的面板、按钮与参数"
          : "This instrument's panels, buttons and parameters in Blender";
      // 不在产品矩阵里的模块（目前只有 Unreal 的 Singer）标成附赠模块
      const bonus = meta
        ? ""
        : `<div class="chip-row"><span class="chip">${
            isZh ? "附赠模块（非乐器产品）" : "Bonus module (not an instrument product)"
          }</span></div>`;
      return `<article class="feature-card">
  <h3>${mark} <span>${escapeHtml(pickText(d.title, lang))}</span></h3>
  <p class="muted">${escapeHtml(note)}</p>
  ${bonus}
  <div class="feature-actions">
    <a class="btn secondary" href="${KIND_PATH[kind]}${d.slug}.html">${
      isZh ? "查看该模块的界面说明" : "Open this module's UI guide"
    }</a>
  </div>
</article>`;
    })
    .join("\n");
  return `<section class="tut-section">
<h2 id="modules">${
    isUnreal
      ? isZh
        ? "按乐器查看 Unreal 端界面说明"
        : "Per-instrument Unreal UI guides"
      : isZh
        ? "按乐器查看专属界面说明"
        : "Per-instrument UI guides"
  }</h2>
<p class="tut-p">${
    isZh
      ? "上面讲的是所有模块共用的那一套框架，各自再加一块专属内容。下面进入各模块的界面说明，逐区讲解它们独有的页签、按钮与参数。"
      : "Above is the framework every module shares; each one adds its own content on top. Open the guides below for a section-by-section walkthrough of their own tabs, buttons and parameters."
  }</p>
<div class="grid grid-3">${cards}</div>
<div class="feature-actions">
  <a class="btn link" href="${KIND_ROOT[otherKind]}">${
    isZh
      ? `换平台：${KIND_LABEL[otherKind].zh}总览`
      : `Other platform: ${KIND_LABEL[otherKind].en} overview`
  }</a>
</div>
</section>`;
}

/**
 * 「软件界面详细说明」页尾导航：
 * 每个产品只有一篇，所以不给上一篇 / 下一篇，只给「回到快速上手 + 切换产品 + 总入口」，
 * 避免读者在 7 篇参数说明之间迷路。
 */
function renderDetailPager(
  kind: TutorialKind,
  doc: TutorialDoc,
  all: TutorialDoc[],
  lang: Lang,
): string {
  const isZh = lang === "zh";
  const list = all.filter((d) => !NON_PRODUCT_SLUGS.has(d.slug));
  const idx = list.findIndex((d) => d.slug === doc.slug);
  const prev = idx > 0 ? list[idx - 1] : undefined;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : undefined;

  const switchLink = (d: TutorialDoc | undefined, label: string, dir: string) =>
    d
      ? `<a class="tut-pager-link ${dir}" href="${KIND_PATH[kind]}${d.slug}.html">
  <span class="tut-pager-label">${label}</span>
  <span class="tut-pager-title">${escapeHtml(pickText(d.title, lang))}</span>
</a>`
      : `<span class="tut-pager-link is-empty"></span>`;

  return `<div class="tut-pager-grid">
${switchLink(prev, isZh ? "← 上一个产品" : "← Previous instrument", "prev")}
${switchLink(next, isZh ? "下一个产品 →" : "Next instrument →", "next")}
</div>
<div class="feature-actions">
  <a class="btn secondary" href="/quickstart/${doc.slug}.html">${
    isZh
      ? "先看这一篇：快速上手（最短流程）"
      : "Start here: Quick Start (shortest flow)"
  }</a>
  <a class="btn link" href="/details.html">${
    isZh ? "全部产品的界面说明" : "All UI references"
  }</a>
  <a class="btn link" href="/quickstart.html">${
    isZh ? "全部产品的快速上手" : "All Quick Starts"
  }</a>
  <a class="btn link" href="/products.html">${
    isZh ? "返回产品矩阵" : "Back to products"
  }</a>
</div>`;
}

function renderPager(
  kind: TutorialKind,
  doc: TutorialDoc,
  all: TutorialDoc[],
  lang: Lang,
): string {
  const isZh = lang === "zh";
  const list = all.filter((d) => !NON_PRODUCT_SLUGS.has(d.slug));
  const idx = list.findIndex((d) => d.slug === doc.slug);
  const prev = idx > 0 ? list[idx - 1] : undefined;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : undefined;
  // 只判断另一类里有没有同名页面（浅目录），不加载那一类的正文
  const hasCross = hasDoc(crossKind(kind), doc.slug);

  const link = (d: TutorialDoc | undefined, label: string, dir: string) =>
    d
      ? `<a class="tut-pager-link ${dir}" href="${KIND_PATH[kind]}${d.slug}.html">
  <span class="tut-pager-label">${label}</span>
  <span class="tut-pager-title">${productMarkBySlug(d.slug)}<span>${escapeHtml(pickText(d.title, lang))}</span></span>
</a>`
      : `<span class="tut-pager-link is-empty"></span>`;

  const crossLink = hasCross
    ? `<a class="tut-pager-cross" href="${KIND_PATH[crossKind(kind)]}${doc.slug}.html">
  ${
    kind === "plugin" || kind === "unreal"
      ? isZh
        ? "同一件事在另一个平台"
        : "The same task on the other platform"
      : isZh
        ? "继续学习"
        : "Continue with"
  }：${escapeHtml(crossLabel(crossKind(kind), lang))}
</a>`
    : "";

  const backLabel =
    kind === "plugin" || kind === "unreal"
      ? isZh
        ? `返回${KIND_LABEL[kind].zh}总览`
        : `Back to the ${KIND_LABEL[kind].en} overview`
      : isZh
        ? "返回教程总览"
        : "Back to overview";

  // 插件手册多给一条「平台选择」入口（/plugin-manuals.html 是通用枢纽页）
  const hubHref =
    kind === "plugin" || kind === "unreal"
      ? `<a class="btn link" href="/plugin-manuals.html">${
          isZh ? "插件手册首页（选平台）" : "Plugin manuals home (pick a platform)"
        }</a>`
      : "";

  const isProduct = PRODUCT_META.some((p) => p.slug === doc.slug);
  const quickHref =
    isProduct && (kind === "plugin" || kind === "unreal")
      ? `<a class="btn link" href="/quickstart/${doc.slug}.html">${
          isZh ? "该产品的 Quick Start" : "This product's Quick Start"
        }</a>`
      : "";

  return `<div class="tut-pager-grid">
${link(prev, isZh ? "← 上一篇" : "← Previous", "prev")}
${link(next, isZh ? "下一篇 →" : "Next →", "next")}
</div>
${crossLink}
<div class="feature-actions">
  <a class="btn link" href="${KIND_ROOT[kind]}">${backLabel}</a>
  ${quickHref}
  ${hubHref}
</div>`;
}

function render(kind: TutorialKind, all: TutorialDoc[]): void {
  const lang = getLang();
  const slug = document.body.dataset.productSlug || "";
  const doc = all.find((d) => d.slug === slug);
  if (!doc) {
    setText("tut-title", `未找到教程内容（${kind} / ${slug}）`);
    return;
  }

  const isZh = lang === "zh";
  const isOverview = doc.slug === OVERVIEW_SLUG;
  const productName = pickText(doc.title, lang);

  document.title = `${productName} · ${kindLabel(kind, lang)} | MusicDoll`;
  setText("tut-eyebrow", kindLabel(kind, lang));
  // 标题里的产品标识统一用「授权前缀方块」（见 product-visual.ts），不再用内容文件里的 emoji
  const titleEl = document.getElementById("tut-title");
  if (titleEl) {
    const mark = productMarkBySlug(doc.slug);
    titleEl.innerHTML = `${mark}<span>${escapeHtml(productName)}</span>`;
  }
  setText("tut-subtitle", pickText(doc.subtitle, lang));
  setText("toc-head-title", isZh ? "本页大纲" : "On this page");
  setText("tut-crumb-current", productName);
  setText("tut-crumb-home", isZh ? "首页" : "Home");
  setText("tut-footer", kind === "plugin"
    ? isZh
      ? "插件报错会输出到 Blender 的系统控制台（窗口 → 切换系统控制台），反馈时请附上该日志。"
      : "Plugin errors are printed to Blender's system console (Window → Toggle System Console); include that log when reporting an issue."
    : kind === "unreal"
      ? isZh
        ? "Unreal 侧的提示多数打在 Output Log（窗口 → 输出日志）里，反馈时请附上该日志。"
        : "On the Unreal side most messages land in the Output Log (Window → Output Log); include that log when reporting an issue."
      : kind === "detail"
        ? isZh
          ? "右下角的界面截图会跟着你正在读的小节自动切换；截图看不清楚可以点窗口右上角收起，读正文更清爽。"
          : "The screenshot panel follows the section you are reading. Collapse it with the ✕ button when you only want the text."
        : isZh
          ? "遇到问题请在控制台点「复制全部」后把日志发给作者。"
          : "If something breaks, click 复制全部 in the console and send the log to the author.");
  const topButton = document.querySelector<HTMLElement>("[data-toc-top]");
  if (topButton) topButton.textContent = isZh ? "回到顶部" : "Back to top";
  const toggle = document.querySelector<HTMLElement>("[data-toc-toggle]");
  if (toggle) toggle.textContent = isZh ? "本页大纲" : "On this page";

  const crumbRoot = document.getElementById(
    "tut-crumb-root",
  ) as HTMLAnchorElement | null;
  if (crumbRoot) {
    const crumb = CRUMB_ROOT[kind];
    crumbRoot.href = crumb.href;
    crumbRoot.textContent = isZh ? crumb.zh : crumb.en;
  }

  // 第三级：仅插件模块页需要（总览页本身即平台层级，避免重复）
  const crumbPlatform = document.getElementById(
    "tut-crumb-platform",
  ) as HTMLAnchorElement | null;
  if (crumbPlatform) {
    const platform = CRUMB_PLATFORM[kind];
    if (!platform || isOverview) {
      // 顺带移除前一个分隔符，避免出现「… / 」
      crumbPlatform.previousElementSibling?.remove();
      crumbPlatform.remove();
    } else {
      crumbPlatform.href = platform.href;
      crumbPlatform.textContent = isZh ? platform.zh : platform.en;
    }
  }

  const meta = document.getElementById("tut-meta");
  if (meta) {
    // 注意：这里不再放 kindLabel —— 它和页头的 eyebrow 是同一句话，
    // 两处并排出现（「软件界面详细说明」×2）只是重复噪音。
    const chips: string[] = [];
    // 免费口径（2026-10-19）：七款软件都能免费下载使用、内置演奏者直接出片，
    // 所以快速上手与界面说明都给一个「免费可用」标签；进阶教程讲的是自定义角色，
    // 那是完整版功能，标「完整版功能」——这也是全站唯一提付费的地方。
    if (kind === "quickstart" || kind === "detail") {
      chips.push(
        `<span class="chip free">${isZh ? "免费可用" : "Free to use"}</span>`,
      );
    }
    if (kind === "advanced") {
      chips.push(
        `<span class="chip">${isZh ? "完整版功能" : "Full version"}</span>`,
      );
    }
    if (kind === "plugin") {
      chips.push(
        `<span class="chip free">${
          isZh ? "Blender 插件免费" : "Blender plugin is free"
        }</span>`,
      );
    }
    if (kind === "unreal") {
      chips.push(
        `<span class="chip free">${
          isZh ? "Unreal 插件免费" : "Unreal plugin is free"
        }</span>`,
      );
    }
    if (isOverview && (kind === "plugin" || kind === "unreal")) {
      chips.push(
        `<span class="chip">${isZh ? "通用部分" : "Common features"}</span>`,
      );
    }
    if (kind === "detail") {
      chips.push(
        `<span class="chip">${isZh ? "Rust 端软件界面" : "Rust app UI"}</span>`,
      );
    }
    chips.push(
      `<span class="chip">${
        isZh ? `约 ${doc.readMinutes} 分钟` : `~${doc.readMinutes} min`
      }</span>`,
    );
    meta.innerHTML = chips.join("");
  }

  const root = document.querySelector<HTMLElement>("[data-toc-root]");
  if (root) {
    root.innerHTML =
      renderSections(doc.sections, lang) +
      (isOverview ? renderHub(kind, all, lang) : "") +
      renderVideoSection(doc, lang);
  }

  // 正文写完后挂右下角截图悬浮窗（整张图原样显示，不随滚动移动）
  mountScreenshotViewer(doc);

  const pager = document.getElementById("tut-pager");
  if (pager) {
    const otherKind = crossKind(kind);
    pager.innerHTML = isOverview
      ? `<div class="feature-actions">
  <a class="btn secondary" href="${KIND_ROOT[otherKind]}">${
    isZh
      ? `换到${KIND_LABEL[otherKind].zh}`
      : `Switch to the ${KIND_LABEL[otherKind].en}`
  }</a>
  <a class="btn link" href="/plugin-manuals.html">${
    isZh ? "插件手册首页（选平台）" : "Plugin manuals home (pick a platform)"
  }</a>
  <a class="btn link" href="/quickstart.html">${
    isZh ? "去看各产品的 Quick Start" : "Browse the Quick Starts"
  }</a>
  <a class="btn link" href="/products.html">${
    isZh ? "返回产品矩阵" : "Back to products"
  }</a>
  <a class="btn link" href="/buy.html">${isZh ? "购买授权" : "Buy a license"}</a>
</div>`
      : kind === "detail"
        ? renderDetailPager(kind, doc, all, lang)
        : renderPager(kind, doc, all, lang);
  }

  // 正文渲染完成后重建侧边栏（标题文本随语言变化）
  buildToc();
}

/**
 * 页面入口：由 tutorial-quickstart.ts / tutorial-advanced.ts / tutorial-plugin.ts /
 * tutorial-unreal.ts 调用，并传入**本类自己的**正文注册表（保持按类分包）。
 */
export function initTutorialPage(kind: TutorialKind, all: TutorialDoc[]): void {
  document.body.dataset.tutorial = kind;
  initI18n();
  const draw = () => render(kind, all);
  draw();
  document.addEventListener("langchange", draw);
}
